import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../context/LanguageContext";

const RECIPES = {
  fr: [
    {
      key: "bouillabaisse",
      name: "Bouillabaisse safranée",
      sub: "Marseille · plat principal",
      meta: "1 h 30 · 6 personnes",
      intro:
        "Le ragoût de poisson emblématique du Vieux-Port — où le safran de Pampore rencontre la rascasse méditerranéenne. Servir avec rouille et croûtons aillés.",
      ingredients: [
        "2 kg de poissons de roche (rascasse, grondin, congre, vive)",
        "20 stigmates de safran Mongra",
        "2 oignons, 4 gousses d'ail, 4 tomates mûres",
        "1 bulbe de fenouil, 1 zeste d'orange",
        "1 verre de vin blanc sec, persil, thym, laurier",
        "Huile d'olive, sel de Camargue, poivre Tellicherry",
      ],
      steps: [
        "Faire infuser le safran dans 2 cuillères de lait tiède pendant 20 minutes.",
        "Suer oignons, ail et fenouil dans l'huile d'olive sans coloration.",
        "Ajouter tomates concassées, zeste d'orange et fines herbes ; cuire 10 minutes.",
        "Mouiller au vin blanc et à l'eau bouillante, ajouter le safran infusé avec son lait.",
        "Cuire les poissons les plus fermes 15 minutes, puis les délicats 5 minutes.",
        "Servir aussitôt avec une rouille bien relevée et des croûtons frottés à l'ail.",
      ],
    },
    {
      key: "moules",
      name: "Moules safranées au Muscadet",
      sub: "Bretagne · entrée ou plat",
      meta: "25 minutes · 4 personnes",
      intro:
        "La marinière revisitée. Six stigmates suffisent à transformer la coquille en festin doré. Servir avec des frites maison, comme il se doit.",
      ingredients: [
        "2 kg de moules de bouchot",
        "6 stigmates de safran Mongra",
        "2 échalotes ciselées, 2 gousses d'ail",
        "30 g de beurre demi-sel",
        "25 cl de Muscadet ou Sancerre",
        "10 cl de crème fraîche épaisse, 1 bouquet de persil plat",
      ],
      steps: [
        "Faire infuser le safran dans la crème fraîche tiédie pendant 15 minutes.",
        "Nettoyer et gratter les moules sous l'eau froide ; jeter celles qui sont ouvertes.",
        "Suer les échalotes et l'ail dans le beurre fondu, sans coloration.",
        "Mouiller au vin blanc, porter à frémissement, puis ajouter les moules à couvert.",
        "Cuire 5 minutes à feu vif en secouant la cocotte, jusqu'à ouverture complète.",
        "Verser la crème safranée, parsemer de persil ciselé, servir aussitôt.",
      ],
    },
    {
      key: "creme",
      name: "Crème brûlée au safran",
      sub: "Île-de-France · dessert",
      meta: "30 min + 4 h de repos · 6 ramequins",
      intro:
        "Le dessert le plus français rehaussé par six stigmates. Le safran apporte une note miellée qui se marie au sucre caramélisé comme une évidence.",
      ingredients: [
        "50 cl de crème liquide entière",
        "6 jaunes d'œufs frais",
        "80 g de sucre + 60 g de cassonade pour brûler",
        "6 stigmates de safran Mongra",
        "1 gousse de vanille de Madagascar fendue",
      ],
      steps: [
        "Faire infuser le safran et la gousse de vanille fendue dans la crème tiédie 30 minutes.",
        "Filtrer la crème, la réchauffer doucement sans la faire bouillir.",
        "Blanchir les jaunes avec le sucre, verser la crème chaude en remuant délicatement.",
        "Répartir dans les ramequins, cuire au bain-marie à 100 °C pendant 45 minutes.",
        "Refroidir au moins 4 heures au réfrigérateur.",
        "Au moment de servir, saupoudrer de cassonade et brûler au chalumeau.",
      ],
    },
  ],
  en: [
    {
      key: "bouillabaisse",
      name: "Saffron Bouillabaisse",
      sub: "Marseille · main course",
      meta: "1 h 30 · serves 6",
      intro:
        "The iconic fish stew of the Vieux-Port — where Pampore saffron meets Mediterranean rockfish. Serve with rouille and garlic-rubbed croutons.",
      ingredients: [
        "2 kg rockfish (rascasse, gurnard, conger, weever)",
        "20 stigmas of Mongra saffron",
        "2 onions, 4 garlic cloves, 4 ripe tomatoes",
        "1 fennel bulb, 1 orange zest",
        "1 glass dry white wine, parsley, thyme, bay leaf",
        "Olive oil, Camargue sea salt, Tellicherry pepper",
      ],
      steps: [
        "Steep the saffron in 2 spoons of warm milk for 20 minutes.",
        "Sweat onions, garlic and fennel in olive oil without colouring.",
        "Add crushed tomatoes, orange zest and herbs; cook 10 minutes.",
        "Pour in white wine and boiling water, add the steeped saffron with its milk.",
        "Cook the firmer fish for 15 minutes, then the delicate ones for 5 minutes.",
        "Serve immediately with a bold rouille and garlic-rubbed croutons.",
      ],
    },
    {
      key: "moules",
      name: "Saffron Mussels in Muscadet",
      sub: "Brittany · starter or main",
      meta: "25 minutes · serves 4",
      intro:
        "Marinière reinvented. Six stigmas are enough to turn the humble shell into a golden feast. Serve with proper hand-cut frites.",
      ingredients: [
        "2 kg bouchot mussels",
        "6 stigmas of Mongra saffron",
        "2 finely chopped shallots, 2 garlic cloves",
        "30 g salted butter",
        "25 cl Muscadet or Sancerre",
        "10 cl crème fraîche, a bunch of flat-leaf parsley",
      ],
      steps: [
        "Steep the saffron in the warmed crème fraîche for 15 minutes.",
        "Clean and scrape the mussels under cold water; discard any open ones.",
        "Sweat the shallots and garlic in melted butter, without colouring.",
        "Pour in the white wine, bring to a simmer, then add the mussels covered.",
        "Cook 5 minutes over high heat, shaking the pot, until fully opened.",
        "Pour in the saffron cream, scatter chopped parsley, serve immediately.",
      ],
    },
    {
      key: "creme",
      name: "Saffron Crème Brûlée",
      sub: "Île-de-France · dessert",
      meta: "30 min + 4 h chilling · 6 ramekins",
      intro:
        "The most French of desserts, lifted by six stigmas. Saffron adds a honeyed note that marries the caramelised sugar like an obvious truth.",
      ingredients: [
        "50 cl heavy cream",
        "6 fresh egg yolks",
        "80 g sugar + 60 g brown sugar to caramelise",
        "6 stigmas of Mongra saffron",
        "1 split Madagascar vanilla bean",
      ],
      steps: [
        "Steep the saffron and split vanilla in the warmed cream for 30 minutes.",
        "Strain the cream, warm gently without bringing to a boil.",
        "Whisk yolks with sugar until pale, slowly pour in the warm cream.",
        "Divide between ramekins, bake in a bain-marie at 100 °C for 45 minutes.",
        "Chill for at least 4 hours in the refrigerator.",
        "Just before serving, sprinkle brown sugar and torch to caramelise.",
      ],
    },
  ],
};

const SaffronRecipes = () => {
  const { lang } = useLang();
  const list = RECIPES[lang];
  const [active, setActive] = useState(0);
  const r = list[active];

  return (
    <section
      className="px-6 md:px-12 lg:px-24 py-16 md:py-20 bg-[#F2EFE9]"
      data-testid="saffron-recipes"
    >
      <div className="grid lg:grid-cols-12 gap-8 mb-8">
        <div className="lg:col-span-5">
          <p className="overline mb-3 text-[10px]">
            {lang === "fr" ? "Trois recettes" : "Three recipes"}
          </p>
          <h2 className="font-serif-display text-3xl md:text-4xl font-light leading-[1.1] text-[#1E2B3C]">
            {lang === "fr" ? "Le safran, à la française." : "Saffron, à la française."}
          </h2>
        </div>
        <p className="lg:col-span-6 lg:col-start-7 text-[#5C5C5C] leading-relaxed text-sm md:text-base self-end max-w-xl">
          {lang === "fr"
            ? "Trois plats classiques où une pincée de Pampore change tout — du Vieux-Port à la rive gauche."
            : "Three classic dishes where a pinch of Pampore changes everything — from the Vieux-Port to the rive gauche."}
        </p>
      </div>

      {/* Tabs */}
      <div
        className="border-y border-[#E5E1D8] overflow-x-auto mb-10"
        data-testid="recipe-tabs"
      >
        <div className="flex w-max md:w-full">
          {list.map((rec, i) => (
            <button
              key={rec.key}
              onClick={() => setActive(i)}
              className={`md:flex-1 flex-shrink-0 w-[240px] md:w-auto md:min-w-[200px] text-left px-5 md:px-6 py-4 border-r border-[#E5E1D8] last:border-r-0 transition-colors ${
                active === i
                  ? "bg-[#1E2B3C] text-[#FAF9F6]"
                  : "text-[#1A1A1A] hover:bg-[#E8E2D2]"
              }`}
              data-testid={`recipe-tab-${rec.key}`}
            >
              <p className={`text-[9px] tracking-[0.22em] uppercase mb-1.5 ${active === i ? "text-[#D99A29]" : "text-[#B34A36]"}`}>
                {String(i + 1).padStart(2, "0")} · {rec.sub}
              </p>
              <p className="font-serif-display text-base md:text-lg leading-tight">{rec.name}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Active recipe content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={r.key}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="grid lg:grid-cols-12 gap-8 lg:gap-12"
          data-testid={`recipe-content-${r.key}`}
        >
          <div className="lg:col-span-5">
            <p className="overline text-[9px]">{r.meta}</p>
            <h3 className="font-serif-display text-2xl md:text-3xl font-light mt-2 text-[#1E2B3C]">
              {r.name}
            </h3>
            <p className="mt-4 text-[#1A1A1A]/85 leading-[1.7] text-[14px]">{r.intro}</p>

            <div className="mt-7">
              <p className="overline mb-3 text-[10px]">{lang === "fr" ? "Ingrédients" : "Ingredients"}</p>
              <ul className="space-y-1.5 text-[13px] text-[#1A1A1A]">
                {r.ingredients.map((ing, i) => (
                  <li key={i} className="flex gap-2.5 leading-relaxed">
                    <span className="text-[#B34A36] mt-0.5">·</span>
                    <span>{ing}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <p className="overline mb-4 text-[10px]">{lang === "fr" ? "La méthode" : "The method"}</p>
            <ol className="space-y-4">
              {r.steps.map((step, i) => (
                <li key={i} className="grid grid-cols-[auto_1fr] gap-4" data-testid={`recipe-step-${i + 1}`}>
                  <span className="font-serif-display italic text-2xl text-[#B34A36] leading-none w-8">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-[#1A1A1A]/85 leading-[1.6] text-[13.5px] pt-0.5">{step}</p>
                </li>
              ))}
            </ol>
          </div>
        </motion.div>
      </AnimatePresence>
    </section>
  );
};

export default SaffronRecipes;
